import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  PenTool,
  PenLine,
  Calendar,
  Type,
  CheckSquare,
  ChevronLeft,
  ChevronRight,
  ZoomIn,
  ZoomOut,
  Info,
  Layers,
  Plus,
  Minus,
  FileText,
  UserCircle,
} from "lucide-react";
import FieldPlacementEditor, {
  FieldDef,
  FIELD_TYPES,
} from "../FieldPlacementEditor";
import { documentsApi } from "../../../services/api";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface DocumentInput {
  documentId: string;
  title: string;
  requiresSignature: boolean;
}

interface SignerInput {
  name: string;
  email: string;
  role: string;
  order: number;
  color: string;
}

interface PlaceFieldsData {
  documents: DocumentInput[];
  signers: SignerInput[];
  fields: FieldDef[];
}

interface PlaceFieldsStepProps {
  data: PlaceFieldsData;
  onChange: (data: { fields: FieldDef[] }) => void;
}

// ---------------------------------------------------------------------------
// Signer colors (used to identify signers visually)
// ---------------------------------------------------------------------------

const SIGNER_COLORS = [
  "#3B82F6",
  "#10B981",
  "#F59E0B",
  "#EF4444",
  "#8B5CF6",
  "#EC4899",
];

const SENDER_COLOR = "#374151"; // slate-700 for sender fields
const SENDER_SIGNER_INDEX = -1;

// ---------------------------------------------------------------------------
// Zoom levels
// ---------------------------------------------------------------------------

const ZOOM_LEVELS = [0.5, 0.6, 0.75, 0.9, 1.0, 1.15, 1.25];
const DEFAULT_ZOOM_INDEX = 2; // 0.75

// ---------------------------------------------------------------------------
// Field icon map for the palette
// ---------------------------------------------------------------------------

const FIELD_ICON_MAP: Record<string, React.FC<{ className?: string }>> = {
  signature: PenTool,
  initials: PenLine,
  date_signed: Calendar,
  text: Type,
  checkbox: CheckSquare,
};

// ---------------------------------------------------------------------------
// PlaceFieldsStep
// ---------------------------------------------------------------------------

const PlaceFieldsStep: React.FC<PlaceFieldsStepProps> = ({ data, onChange }) => {
  const [activeDocIndex, setActiveDocIndex] = useState(0);
  const [activeSignerIndex, setActiveSignerIndex] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [zoomIndex, setZoomIndex] = useState(DEFAULT_ZOOM_INDEX);
  const [totalPages, setTotalPages] = useState<Record<string, number>>({});
  const [showSignerDropdown, setShowSignerDropdown] = useState(false);
  const [documentUrls, setDocumentUrls] = useState<Record<number, string>>({});

  const scale = ZOOM_LEVELS[zoomIndex];

  // Documents with signature requirement only
  const documents = data.documents || [];
  const signers = data.signers || [];
  const fields = data.fields || [];

  // -----------------------------------------------------------------------
  // Fetch document PDFs as authenticated blobs and create blob URLs
  // -----------------------------------------------------------------------

  const blobUrlsRef = useRef<string[]>([]);

  useEffect(() => {
    if (documents.length === 0) return;

    let cancelled = false;

    const fetchDocumentBlobs = async () => {
      const urls: Record<number, string> = {};
      const newBlobUrls: string[] = [];

      await Promise.all(
        documents.map(async (doc, idx) => {
          if (!doc.documentId) return;
          try {
            const res = await documentsApi.get(doc.documentId);
            const versions = res.data?.versions || res.data?.data?.versions;
            if (versions && versions.length > 0) {
              const latestVersion = versions[0];
              const storageKey = latestVersion.storage?.key;
              if (storageKey) {
                // Fetch the PDF as a blob through the authenticated API
                const blobRes = await documentsApi.downloadBlob(storageKey);
                const blob = new Blob([blobRes.data], { type: "application/pdf" });
                const blobUrl = URL.createObjectURL(blob);
                newBlobUrls.push(blobUrl);
                urls[idx] = blobUrl;
              }
            }
          } catch (err) {
            console.warn(`Failed to fetch document ${doc.documentId} for PDF preview:`, err);
          }
        })
      );

      if (!cancelled) {
        // Revoke OLD blob URLs before setting new ones
        blobUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
        blobUrlsRef.current = newBlobUrls;
        setDocumentUrls(urls);
      } else {
        // If cancelled, revoke the new ones we just created
        newBlobUrls.forEach((url) => URL.revokeObjectURL(url));
      }
    };

    fetchDocumentBlobs();

    return () => {
      cancelled = true;
    };
    // Re-fetch if the document list changes (compare by IDs)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documents.map((d) => d.documentId).join(",")]);

  // Cleanup all blob URLs on unmount
  useEffect(() => {
    return () => {
      blobUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    };
  }, []);

  // -----------------------------------------------------------------------
  // PDF page count callback — updates totalPages when PDF reports its count
  // -----------------------------------------------------------------------

  const handlePageCountChange = useCallback(
    (count: number) => {
      setTotalPages((prev) => ({
        ...prev,
        [`${activeDocIndex}`]: count,
      }));
    },
    [activeDocIndex]
  );

  // Must be above early returns to satisfy rules-of-hooks
  const fieldSummary = useMemo(() => {
    const summary: Record<number, Record<number, string[]>> = {};
    fields.forEach((f) => {
      if (!summary[f.documentIndex]) summary[f.documentIndex] = {};
      if (!summary[f.documentIndex][f.signerIndex]) summary[f.documentIndex][f.signerIndex] = [];
      summary[f.documentIndex][f.signerIndex].push(f.label || f.type);
    });
    return summary;
  }, [fields]);

  const totalFieldCount = fields.length;

  // -----------------------------------------------------------------------
  // No documents fallback
  // -----------------------------------------------------------------------

  if (documents.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="inline-flex items-center justify-center h-16 w-16 rounded-2xl bg-slate-100 mb-4">
          <FileText className="h-8 w-8 text-slate-400" />
        </div>
        <h3 className="text-lg font-semibold text-slate-700 mb-2">
          No Documents Added
        </h3>
        <p className="text-sm text-slate-500 max-w-md mx-auto">
          Add documents in the previous step before placing signature fields.
        </p>
      </div>
    );
  }

  // No signers fallback
  if (signers.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="inline-flex items-center justify-center h-16 w-16 rounded-2xl bg-slate-100 mb-4">
          <PenTool className="h-8 w-8 text-slate-400" />
        </div>
        <h3 className="text-lg font-semibold text-slate-700 mb-2">
          No Signers Added
        </h3>
        <p className="text-sm text-slate-500 max-w-md mx-auto">
          Add signers in the previous step before placing fields.
        </p>
      </div>
    );
  }

  // -----------------------------------------------------------------------
  // Derived state
  // -----------------------------------------------------------------------

  const docKey = `${activeDocIndex}`;
  const docTotalPages = totalPages[docKey] || 1;
  const isSenderMode = activeSignerIndex === SENDER_SIGNER_INDEX;
  const activeSigner = isSenderMode ? null : (signers[activeSignerIndex] || signers[0]);
  const signerColor = isSenderMode
    ? SENDER_COLOR
    : activeSigner?.color || SIGNER_COLORS[activeSignerIndex % SIGNER_COLORS.length];

  // -----------------------------------------------------------------------
  // Handlers
  // -----------------------------------------------------------------------

  const handleFieldsChange = (updatedFields: FieldDef[]) => {
    onChange({ fields: updatedFields });
  };

  const handleZoomIn = () => {
    setZoomIndex((prev) => Math.min(prev + 1, ZOOM_LEVELS.length - 1));
  };

  const handleZoomOut = () => {
    setZoomIndex((prev) => Math.max(prev - 1, 0));
  };

  const handlePagePrev = () => {
    setCurrentPage((p) => Math.max(1, p - 1));
  };

  const handlePageNext = () => {
    setCurrentPage((p) => Math.min(p + 1, docTotalPages));
  };

  const handleAddPage = () => {
    setTotalPages((prev) => ({
      ...prev,
      [docKey]: (prev[docKey] || 1) + 1,
    }));
  };

  const handleRemovePage = () => {
    if (docTotalPages <= 1) return;
    // Remove fields on the last page
    const lastPage = docTotalPages;
    const filtered = fields.filter(
      (f) => !(f.documentIndex === activeDocIndex && f.page === lastPage)
    );
    onChange({ fields: filtered });
    setTotalPages((prev) => ({
      ...prev,
      [docKey]: Math.max(1, (prev[docKey] || 1) - 1),
    }));
    if (currentPage > docTotalPages - 1) {
      setCurrentPage(Math.max(1, docTotalPages - 1));
    }
  };

  const handleDocChange = (idx: number) => {
    setActiveDocIndex(idx);
    setCurrentPage(1);
  };

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------

  return (
    <div className="flex flex-col" style={{ minHeight: 500 }}>
      {/* Skip hint */}
      <div className="flex items-center gap-2 px-3 py-2 mb-3 bg-blue-50 text-blue-700 rounded-xl text-xs">
        <Info className="h-3.5 w-3.5 flex-shrink-0" />
        <span>
          Drag field types from the left palette onto the document page. You can
          skip this step and place fields later.
        </span>
      </div>

      {/* Main editor layout */}
      <div className="flex rounded-xl border border-slate-200 bg-slate-50 overflow-hidden" style={{ minHeight: 460 }}>
        {/* --------------------------------------------------------------- */}
        {/* Left Palette */}
        {/* --------------------------------------------------------------- */}
        <div className="w-48 flex-shrink-0 border-r border-slate-200 bg-white p-3 flex flex-col gap-2">
          <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">
            Field Types
          </div>

          {FIELD_TYPES.map((ft) => {
            const Icon = FIELD_ICON_MAP[ft.type] || Type;
            return (
              <div
                key={ft.type}
                draggable
                onDragStart={(e) => {
                  e.dataTransfer.setData("fieldType", ft.type);
                  e.dataTransfer.effectAllowed = "copy";
                }}
                className="bg-white border border-slate-200 rounded-lg p-3 cursor-grab hover:shadow-md hover:border-slate-300 transition-all active:cursor-grabbing group"
              >
                <div className="flex items-center gap-2">
                  <div
                    className="h-8 w-8 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: `${signerColor}15`, color: signerColor }}
                  >
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-700 truncate">
                      {ft.label}
                    </div>
                    <div className="text-[10px] text-slate-400 group-hover:text-slate-500 transition-colors">
                      Drag to add
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

          {/* Placed count */}
          <div className="mt-auto pt-3 border-t border-slate-100">
            <div className="flex items-center gap-1.5 text-xs text-slate-500">
              <Layers className="h-3.5 w-3.5" />
              <span>
                {totalFieldCount} field{totalFieldCount !== 1 ? "s" : ""} placed
              </span>
            </div>
          </div>
        </div>

        {/* --------------------------------------------------------------- */}
        {/* Right panel: toolbar + editor */}
        {/* --------------------------------------------------------------- */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Toolbar */}
          <div className="flex items-center gap-3 px-4 py-2.5 border-b border-slate-200 bg-white flex-shrink-0 flex-wrap">
            {/* Document tabs */}
            <div className="flex items-center gap-1 mr-2">
              {documents.map((doc, idx) => (
                <button
                  key={idx}
                  onClick={() => handleDocChange(idx)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                    activeDocIndex === idx
                      ? "text-white shadow-sm"
                      : "text-slate-600 bg-slate-100 hover:bg-slate-200"
                  }`}
                  style={
                    activeDocIndex === idx
                      ? { backgroundColor: "#0F5132" }
                      : undefined
                  }
                >
                  <FileText className="h-3 w-3" />
                  {doc.title || `Doc ${idx + 1}`}
                </button>
              ))}
            </div>

            {/* Divider */}
            <div className="h-5 w-px bg-slate-200" />

            {/* Signer selector */}
            <div className="relative">
              <button
                onClick={() => setShowSignerDropdown(!showSignerDropdown)}
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium bg-white border border-slate-200 hover:border-slate-300 transition-colors"
              >
                {isSenderMode ? (
                  <UserCircle className="w-3.5 h-3.5 text-slate-600 flex-shrink-0" />
                ) : (
                  <span
                    className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                    style={{ backgroundColor: signerColor }}
                  />
                )}
                <span className="text-slate-700 max-w-[120px] truncate">
                  {isSenderMode
                    ? "Me (Sender)"
                    : activeSigner?.name || `Signer ${activeSignerIndex + 1}`}
                </span>
                <ChevronRight
                  className={`h-3 w-3 text-slate-400 transition-transform ${
                    showSignerDropdown ? "rotate-90" : ""
                  }`}
                />
              </button>

              {/* Signer dropdown */}
              <AnimatePresence>
                {showSignerDropdown && (
                  <motion.div
                    initial={{ opacity: 0, y: -4 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -4 }}
                    transition={{ duration: 0.12 }}
                    className="absolute top-full left-0 mt-1 bg-white rounded-xl border border-slate-200 shadow-lg z-30 min-w-[200px] py-1"
                  >
                    {/* Me (Sender) option */}
                    <button
                      onClick={() => {
                        setActiveSignerIndex(SENDER_SIGNER_INDEX);
                        setShowSignerDropdown(false);
                      }}
                      className={`flex items-center gap-2 w-full px-3 py-2 text-xs text-left transition-colors border-b border-slate-100 ${
                        isSenderMode
                          ? "bg-slate-50 font-semibold"
                          : "hover:bg-slate-50"
                      }`}
                    >
                      <UserCircle className="w-3.5 h-3.5 text-slate-600 flex-shrink-0" />
                      <span className="text-slate-700 truncate flex-1">
                        Me (Sender)
                      </span>
                      <span className="text-[10px] text-slate-400">
                        Pre-fill
                      </span>
                    </button>

                    {/* Signer options */}
                    {signers.map((signer, idx) => {
                      const c = signer.color || SIGNER_COLORS[idx % SIGNER_COLORS.length];
                      return (
                        <button
                          key={idx}
                          onClick={() => {
                            setActiveSignerIndex(idx);
                            setShowSignerDropdown(false);
                          }}
                          className={`flex items-center gap-2 w-full px-3 py-2 text-xs text-left transition-colors ${
                            activeSignerIndex === idx
                              ? "bg-slate-50 font-semibold"
                              : "hover:bg-slate-50"
                          }`}
                        >
                          <span
                            className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                            style={{ backgroundColor: c }}
                          />
                          <span className="text-slate-700 truncate flex-1">
                            {signer.name || `Signer ${idx + 1}`}
                          </span>
                          <span className="text-[10px] text-slate-400 capitalize">
                            {signer.role}
                          </span>
                        </button>
                      );
                    })}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Divider */}
            <div className="h-5 w-px bg-slate-200" />

            {/* Zoom controls */}
            <div className="flex items-center gap-1">
              <button
                onClick={handleZoomOut}
                disabled={zoomIndex === 0}
                className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ZoomOut className="h-3.5 w-3.5" />
              </button>
              <span className="text-xs font-medium text-slate-600 w-10 text-center">
                {Math.round(scale * 100)}%
              </span>
              <button
                onClick={handleZoomIn}
                disabled={zoomIndex === ZOOM_LEVELS.length - 1}
                className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ZoomIn className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>

          {/* Editor area with scroll */}
          <div className="flex-1 overflow-auto p-4 flex flex-col items-center" style={{ maxHeight: 520 }}>
            <FieldPlacementEditor
              fields={fields}
              onFieldsChange={handleFieldsChange}
              signers={signers.map((s, i) => ({
                name: s.name || `Signer ${i + 1}`,
                color: s.color || SIGNER_COLORS[i % SIGNER_COLORS.length],
              }))}
              senderColor={SENDER_COLOR}
              currentDocumentIndex={activeDocIndex}
              currentPage={currentPage}
              activeSigner={activeSignerIndex}
              scale={scale}
              documentUrl={documentUrls[activeDocIndex]}
              onPageCountChange={handlePageCountChange}
            />

            {/* Page navigation */}
            <div className="flex items-center gap-3 mt-4">
              <button
                onClick={handlePagePrev}
                disabled={currentPage <= 1}
                className="p-1.5 rounded-lg text-slate-500 hover:bg-white hover:shadow-sm border border-transparent hover:border-slate-200 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>

              <span className="text-sm font-medium text-slate-600">
                Page {currentPage} of {docTotalPages}
              </span>

              <button
                onClick={handlePageNext}
                disabled={currentPage >= docTotalPages}
                className="p-1.5 rounded-lg text-slate-500 hover:bg-white hover:shadow-sm border border-transparent hover:border-slate-200 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ChevronRight className="h-4 w-4" />
              </button>

              {/* Add / remove page — only shown when no PDF URL (manual mode) */}
              {!documentUrls[activeDocIndex] && (
                <>
                  <div className="h-4 w-px bg-slate-300 mx-1" />

                  <button
                    onClick={handleAddPage}
                    className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium text-slate-600 bg-white border border-slate-200 hover:border-slate-300 hover:shadow-sm transition-all"
                  >
                    <Plus className="h-3 w-3" />
                    Page
                  </button>
                  {docTotalPages > 1 && (
                    <button
                      onClick={handleRemovePage}
                      className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium text-slate-600 bg-white border border-slate-200 hover:border-red-300 hover:text-red-600 hover:shadow-sm transition-all"
                    >
                      <Minus className="h-3 w-3" />
                      Page
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* --------------------------------------------------------------- */}
      {/* Field Summary */}
      {/* --------------------------------------------------------------- */}
      {totalFieldCount > 0 && (
        <div className="mt-4 bg-white border border-slate-200 rounded-xl p-4">
          <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
            <Layers className="h-4 w-4 text-slate-400" />
            Field Summary
          </h4>
          <div className="space-y-3">
            {documents.map((doc, docIdx) => {
              const docFields = fieldSummary[docIdx];
              if (!docFields) return null;

              const docFieldCount = Object.values(docFields).reduce(
                (sum, arr) => sum + arr.length,
                0
              );

              return (
                <div key={docIdx}>
                  <div className="text-xs font-semibold text-slate-600 mb-1.5">
                    {doc.title || `Document ${docIdx + 1}`}: {docFieldCount} field
                    {docFieldCount !== 1 ? "s" : ""}
                  </div>
                  <div className="space-y-1 pl-3">
                    {Object.entries(docFields).map(([signerIdxStr, fieldLabels]) => {
                      const signerIdx = parseInt(signerIdxStr, 10);
                      const isSender = signerIdx === SENDER_SIGNER_INDEX;
                      const signer = isSender ? null : signers[signerIdx];
                      const c = isSender
                        ? SENDER_COLOR
                        : signer?.color || SIGNER_COLORS[signerIdx % SIGNER_COLORS.length];

                      return (
                        <div key={signerIdx} className="flex items-center gap-2 text-xs">
                          {isSender ? (
                            <UserCircle className="w-3 h-3 flex-shrink-0 text-slate-600" />
                          ) : (
                            <span
                              className="w-2 h-2 rounded-full flex-shrink-0"
                              style={{ backgroundColor: c }}
                            />
                          )}
                          <span className="text-slate-600 font-medium">
                            {isSender
                              ? "Me (Sender):"
                              : `${signer?.name || `Signer ${signerIdx + 1}`}${signer?.role ? ` (${signer.role})` : ""}:`}
                          </span>
                          <span className="text-slate-500">
                            {fieldLabels.join(", ")}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default PlaceFieldsStep;
